import React, { useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { type IconProps } from '@atlaskit/icon/types';
import { FadeIn, StaggeredEntrance } from '@atlaskit/motion';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type ConversationAction, type SkillMetadataMap } from '../../../../common/types';
import { DynamicSurfaceActionItem } from '../../../../common/ui/action-item';
import { ActionItemSkeleton } from '../../../../common/ui/action-item-skeleton';
import { isPlaceholderAction } from '../../../../common/utils/create-placeholder-action';
import { orderRankedActions } from '../../../../common/utils/order-ranked-actions';

const styles = cssMap({
	actionContainerStyles: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.050'),
		paddingTop: token('space.050'),
		width: '100%',
		display: 'flex',
	},
	sideBarActionContainerStyles: {
		maxHeight: '464px',
	},
	actionStackStyles: {
		width: '100%',
	},
	sideBarActionStackStyles: {
		overflowY: 'auto',
	},
});

export const i18n: {
	showLessActionText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	showLessActionText: {
		id: 'rovo-conversation-actions.actions.show-less-action-text',
		defaultMessage: 'Show less',
		description: 'The action text for the show less action in rovo conversation actions',
	},
});

export type DiscoverPromptLibraryButtonProps = {
	title: string;
	icon: (props: IconProps) => React.JSX.Element;
	onClick: () => void;
	badge?: React.ReactNode;
};

type ConversationActionExpandedViewProps = {
	actions: ConversationAction[];
	showLessButton?: boolean;
	handleShowLess: () => void;
	handleShowLessHover: () => void;
	handleShowLessLeave: () => void;
	isInSideBar?: boolean;
	actionRankingOrder?: string[] | null;
	handleAction: (action: ConversationAction, index?: number) => void;
	handleActionHover: (action: ConversationAction, index?: number) => void;
	handleActionLeave: () => void;
	skillMetadataMap?: SkillMetadataMap;
	discoverPromptLibraryButton?: DiscoverPromptLibraryButtonProps;
};

export const ConversationActionExpandedView = ({
	actions,
	showLessButton,
	handleShowLess,
	handleShowLessHover,
	handleShowLessLeave,
	isInSideBar,
	actionRankingOrder,
	handleAction,
	handleActionHover,
	handleActionLeave,
	skillMetadataMap,
	discoverPromptLibraryButton,
}: ConversationActionExpandedViewProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const orderedActions = useMemo(() => {
		if (actionRankingOrder) {
			return orderRankedActions(actionRankingOrder, actions);
		}
		return actions;
	}, [actionRankingOrder, actions]);

	return (
		<Box
			xcss={cx(styles.actionContainerStyles, isInSideBar && styles.sideBarActionContainerStyles)}
		>
			<Stack
				role="list"
				xcss={cx(styles.actionStackStyles, isInSideBar && styles.sideBarActionStackStyles)}
			>
				{orderedActions.length > 0 && (
					<StaggeredEntrance columns={1}>
						<>
							{orderedActions.map((action, index) => {
								if (isPlaceholderAction(action)) {
									return <ActionItemSkeleton key={action.id} action={action} />;
								}

								const actionItem = (
									<FadeIn key={`action-item-expanded-view-${index}`} duration="medium">
										{(props) => (
											<div {...props} role="listitem">
												<DynamicSurfaceActionItem
													key={action.id}
													title={action.title}
													icon={action.icon}
													onClick={() => handleAction(action, index)}
													onMouseEnter={() => handleActionHover(action, index)}
													onMouseLeave={handleActionLeave}
													ref={action.ref}
													href={action.href}
													action={action}
													skillMetadataMap={skillMetadataMap}
													badge={action.badge}
												/>
											</div>
										)}
									</FadeIn>
								);

								if (showLessButton && index === orderedActions.length - 1) {
									return (
										<React.Fragment key={`expanded-actions-with-show-less-${index}`}>
											{actionItem}
											{discoverPromptLibraryButton ? (
												<FadeIn key="discover-prompt-library-button" duration="large">
													{(props) => (
														<div {...props} role="listitem">
															<DynamicSurfaceActionItem
																key="discover-prompt-library"
																title={discoverPromptLibraryButton.title}
																icon={discoverPromptLibraryButton.icon}
																onClick={discoverPromptLibraryButton.onClick}
																badge={discoverPromptLibraryButton.badge}
															/>
														</div>
													)}
												</FadeIn>
											) : (
												<></>
											)}
											<FadeIn key={`show-less-button`} duration="large">
												{(props) => (
													<div {...props} role="listitem">
														<DynamicSurfaceActionItem
															key={'show-less-ai-action'}
															title={formatMessage(i18n.showLessActionText)}
															icon={() => (
																<ChevronUpIcon label="" color={token('color.icon.subtlest')} />
															)}
															onClick={handleShowLess}
															onMouseEnter={handleShowLessHover}
															onMouseLeave={handleShowLessLeave}
														/>
													</div>
												)}
											</FadeIn>
										</React.Fragment>
									);
								}

								return actionItem;
							})}
							{!showLessButton && discoverPromptLibraryButton ? (
								<FadeIn key="discover-prompt-library-button" duration="large">
									{(props) => (
										<div {...props} role="listitem">
											<DynamicSurfaceActionItem
												key="discover-prompt-library"
												title={discoverPromptLibraryButton.title}
												icon={discoverPromptLibraryButton.icon}
												onClick={discoverPromptLibraryButton.onClick}
												badge={discoverPromptLibraryButton.badge}
											/>
										</div>
									)}
								</FadeIn>
							) : (
								<></>
							)}
						</>
					</StaggeredEntrance>
				)}
			</Stack>
		</Box>
	);
};
